import { useEffect, useMemo, useState } from 'react';
import {
  formatModelCode,
  modelMatchesCodeSearch,
  parseModelCodeQuery,
} from '../utils/modelCategoryRanges';
import { MOBILE_VIEWPORT_MQ, useIsMobileViewport } from '../hooks/useIsMobileViewport';

const STORAGE_KEY_MOBILE = 'modelPickerViewMobile';
const STORAGE_KEY_DESKTOP = 'modelPickerViewDesktop';
const LEGACY_STORAGE_KEY = 'modelPickerView';

const getViewStorageKey = (isMobile) => (isMobile ? STORAGE_KEY_MOBILE : STORAGE_KEY_DESKTOP);

const getDefaultViewForViewport = (isMobile) => (isMobile ? 'list' : 'grid');

const loadViewMode = (isMobile) => {
  const saved = localStorage.getItem(getViewStorageKey(isMobile));
  if (saved === 'list' || saved === 'grid') return saved;
  const legacy = localStorage.getItem(LEGACY_STORAGE_KEY);
  if (legacy === 'list' || legacy === 'grid') return legacy;
  return getDefaultViewForViewport(isMobile);
};

const CATEGORY_OPTIONS = [
  { id: '', label: 'All', compactLabel: 'All' },
  { id: 'small', label: 'Small', compactLabel: 'Sm' },
  { id: 'medium', label: 'Medium', compactLabel: 'Med' },
  { id: 'large', label: 'Large', compactLabel: 'Lg' },
  { id: 'pen', label: 'Pen/Artistic', compactLabel: 'Pen' },
];

const ModelPicker = ({
  inventory = [],
  selectedModelId = '',
  onSelect,
  multiSelect = false,
  selectedModelIds = [],
  selectedQtyById = {},
  onToggleSelect,
  disabledIds = [],
  accentRing = 'ring-green-500',
  fillHeight = false,
  scrollBottomInset = '',
  compactMobile = false,
  dynamicScrollInset = false,
  outOfStockLabel = 'Out of stock',
  hidePrice = false,
  resetKey,
}) => {
  const [categoryFilter, setCategoryFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const isMobileViewport = useIsMobileViewport();
  const [viewMode, setViewMode] = useState(() => {
    const isMobile =
      typeof window !== 'undefined' && window.matchMedia(MOBILE_VIEWPORT_MQ).matches;
    return loadViewMode(isMobile);
  });

  useEffect(() => {
    localStorage.setItem(getViewStorageKey(isMobileViewport), viewMode);
  }, [viewMode, isMobileViewport]);

  useEffect(() => {
    setViewMode(loadViewMode(isMobileViewport));
  }, [isMobileViewport]);

  useEffect(() => {
    if (resetKey === undefined || resetKey === null) return;
    setSearchQuery('');
    setCategoryFilter('');
  }, [resetKey]);

  const filteredInventory = useMemo(() => {
    const query = searchQuery.trim();
    const parsed = parseModelCodeQuery(query);
    const queryLower = query.toLowerCase();

    return inventory
      .filter((inv) => {
        if (categoryFilter && inv.size !== categoryFilter) return false;
        if (!query) return true;
        return (
          inv.modelName?.toLowerCase().includes(queryLower) ||
          modelMatchesCodeSearch(inv.size, inv.modelNumber, parsed) ||
          formatModelCode(inv.size, inv.modelNumber).toLowerCase().includes(queryLower)
        );
      })
      .sort((a, b) => (a.modelNumber ?? 0) - (b.modelNumber ?? 0));
  }, [inventory, categoryFilter, searchQuery]);

  const formatMoney = (amount) => `₹${Number(amount || 0).toLocaleString('en-IN')}`;

  const isItemAvailable = (inv) => inv.isAvailable ?? (inv.currentStock ?? 0) > 0;

  const getItemTooltip = (inv) => {
    const code = formatModelCode(inv.size, inv.modelNumber);
    if (hidePrice) return `${inv.modelName} · ${code}`;
    return `${inv.modelName} · ${code} · ${formatMoney(inv.price)}`;
  };

  const getItemState = (inv) => {
    const isSelected = multiSelect
      ? selectedModelIds.includes(inv._id)
      : selectedModelId === inv._id;
    const isDisabled = !multiSelect && disabledIds.includes(inv._id);
    const unavailable = !isItemAvailable(inv);
    return { isSelected, isDisabled, unavailable, isInactive: unavailable || isDisabled };
  };

  const handleItemClick = (inv) => {
    if (multiSelect) {
      onToggleSelect?.(inv);
    } else {
      onSelect?.(inv);
    }
  };

  const itemRowClass = (isSelected, isInactive) =>
    `w-full flex items-center gap-3 p-2 text-left transition-colors ${
      isSelected ? `bg-indigo-50 ring-1 ring-inset ${accentRing}` : 'hover:bg-gray-50'
    } ${isInactive ? 'opacity-50' : ''}`;

  const gridCardClass = (isSelected, isInactive) =>
    `flex flex-col h-full rounded-lg border overflow-hidden text-left transition-colors ${
      isSelected ? `bg-indigo-50 ring-2 ring-inset ${accentRing}` : 'border-gray-200 hover:bg-gray-50'
    } ${isInactive ? 'opacity-50' : ''}`;

  const renderStockLine = (inv, isDisabled, isSelected) => (
    <>
      {hidePrice
        ? isItemAvailable(inv)
          ? `Stock: ${inv.currentStock}`
          : outOfStockLabel
        : (
          <>
            {formatMoney(inv.price)}
            {isItemAvailable(inv) ? ` · Stock: ${inv.currentStock}` : ` · ${outOfStockLabel}`}
          </>
        )}
      {isDisabled && !isSelected ? ' · Already in order' : ''}
    </>
  );

  const renderListTextBlock = (inv, isDisabled, isSelected, listTitleClass) => (
    <>
      <div className="min-w-0 flex-1">
        <div className={listTitleClass}>
          {inv.modelName} · {formatModelCode(inv.size, inv.modelNumber)}
        </div>
        <div className="text-xs text-gray-600">
          {renderStockLine(inv, isDisabled, isSelected)}
        </div>
      </div>
      {isSelected && (
        <span className="text-xs font-semibold text-indigo-600 shrink-0">
          {selectedQtyById[inv._id] ? `×${selectedQtyById[inv._id]}` : 'Selected'}
        </span>
      )}
    </>
  );

  const renderListItem = (inv) => {
    const { isSelected, isDisabled, isInactive } = getItemState(inv);

    const listTitleClass = compactMobile
      ? 'text-sm font-medium text-gray-900 line-clamp-2 leading-snug'
      : 'text-sm font-medium text-gray-900 truncate';

    return (
      <button
        key={inv._id}
        type="button"
        disabled={isInactive}
        onClick={() => handleItemClick(inv)}
        title={compactMobile ? getItemTooltip(inv) : undefined}
        className={`${itemRowClass(isSelected, isInactive)} ${isInactive ? 'cursor-not-allowed' : ''}`}
      >
        {inv.photo ? (
          <img
            src={inv.photo}
            alt=""
            className="w-10 h-10 object-cover rounded border shrink-0"
          />
        ) : (
          <div className="w-10 h-10 rounded border bg-gray-100 shrink-0" />
        )}
        {renderListTextBlock(inv, isDisabled, isSelected, listTitleClass)}
      </button>
    );
  };

  const renderGridImageArea = (inv, isSelected, isUnavailable, imageAspectClass, modelCodeBadgeClass) => {
    const qtyBadge = selectedQtyById[inv._id];

    return (
      <div className={`relative ${imageAspectClass} bg-gray-50 flex items-center justify-center overflow-hidden`}>
        {inv.photo ? (
          <img
            src={inv.photo}
            alt=""
            className={`max-h-full max-w-full object-contain block pointer-events-none${isUnavailable ? ' grayscale opacity-75' : ''}`}
          />
        ) : (
          <div className="w-full h-full bg-gray-100" />
        )}
        {isUnavailable && (
          <>
            <div className="pointer-events-none absolute inset-0 bg-black/25 z-10" />
            <span className="pointer-events-none absolute top-1/2 left-1/2 z-20 -translate-x-1/2 -translate-y-1/2 rounded-full border border-gray-200 bg-white/95 px-2 py-0.5 text-[10px] font-semibold text-gray-700 shadow-sm">
              {outOfStockLabel}
            </span>
          </>
        )}
        {isSelected && (
          <span className="pointer-events-none absolute top-1.5 right-1.5 z-20 text-[10px] font-semibold text-indigo-700 bg-white/90 px-1.5 py-0.5 rounded">
            {qtyBadge ? `×${qtyBadge}` : 'Selected'}
          </span>
        )}
        <span className={`pointer-events-none ${modelCodeBadgeClass} z-20`}>
          {formatModelCode(inv.size, inv.modelNumber)}
        </span>
      </div>
    );
  };

  const renderGridFooter = (inv, isDisabled, isSelected, footerPaddingClass, nameClass, stockClass) => (
    <div className={`${footerPaddingClass} space-y-0.5 min-w-0`}>
      <div className={nameClass}>{inv.modelName}</div>
      <div className={stockClass}>{renderStockLine(inv, isDisabled, isSelected)}</div>
    </div>
  );

  const renderGridItem = (inv) => {
    const { isSelected, isDisabled, isInactive, unavailable } = getItemState(inv);

    const imageAspectClass = compactMobile
      ? 'aspect-[5/4] md:aspect-[5/6]'
      : 'aspect-[5/6]';
    const footerPaddingClass = compactMobile ? 'p-1 md:p-1.5' : 'p-1.5';
    const nameClass = compactMobile
      ? 'text-xs md:text-sm font-semibold text-gray-900 line-clamp-2 leading-tight min-h-[2.5em]'
      : 'text-sm font-semibold text-gray-900 line-clamp-2 leading-tight';
    const stockClass = compactMobile
      ? 'text-[10px] leading-tight text-gray-600 line-clamp-1 md:text-xs md:leading-snug md:line-clamp-none'
      : 'text-xs text-gray-600 leading-snug';
    const modelCodeBadgeClass = compactMobile
      ? 'absolute bottom-1 left-1 text-[10px] font-bold text-white bg-gray-900/80 px-1 py-0.5 rounded md:text-xs md:px-1.5'
      : 'absolute bottom-1 left-1 text-xs font-bold text-white bg-gray-900/80 px-1.5 py-0.5 rounded';

    return (
      <button
        key={inv._id}
        type="button"
        disabled={isInactive}
        onClick={() => handleItemClick(inv)}
        title={compactMobile ? getItemTooltip(inv) : undefined}
        className={`${gridCardClass(isSelected, isInactive)} ${isInactive ? 'cursor-not-allowed' : ''}`}
      >
        {renderGridImageArea(inv, isSelected, unavailable, imageAspectClass, modelCodeBadgeClass)}
        {renderGridFooter(inv, isDisabled, isSelected, footerPaddingClass, nameClass, stockClass)}
      </button>
    );
  };

  const scrollInsetClass = dynamicScrollInset
    ? 'model-picker-scroll-pad'
    : scrollBottomInset;

  const footerSpacerClass =
    dynamicScrollInset &&
    `model-picker-scroll-spacer col-span-full w-full${
      viewMode === 'grid' ? ' model-picker-scroll-spacer--grid' : ''
    }`;

  const footerSpacer = footerSpacerClass ? (
    <div className={footerSpacerClass} aria-hidden="true" />
  ) : null;

  const scrollPaddingClass = compactMobile ? 'p-1.5 md:p-2' : 'p-2';

  const gridScrollClass = fillHeight
    ? `flex-1 min-h-0 overflow-y-auto rounded-md border border-gray-200 bg-white ${scrollPaddingClass} ${scrollInsetClass}`
    : `max-h-72 sm:max-h-80 overflow-y-auto rounded-md border border-gray-200 bg-white ${scrollPaddingClass}`;

  const listScrollClass = fillHeight
    ? `flex-1 min-h-0 overflow-y-auto rounded-md border border-gray-200 divide-y divide-gray-100 bg-white ${scrollInsetClass}`
    : 'max-h-52 overflow-y-auto rounded-md border border-gray-200 divide-y divide-gray-100 bg-white';

  const emptyStateClass = fillHeight
    ? 'flex-1 min-h-0 flex items-center justify-center p-3 text-sm text-gray-500 text-center rounded-md border border-gray-200 bg-white'
    : 'p-3 text-sm text-gray-500 text-center rounded-md border border-gray-200 bg-white';

  const gridColsClass = compactMobile
    ? 'grid grid-cols-3 md:grid-cols-5 lg:grid-cols-6 gap-1 md:gap-2'
    : 'grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-1.5 sm:gap-2';

  const searchRowClass = compactMobile
    ? `flex flex-row items-center gap-2${fillHeight ? ' shrink-0' : ''}`
    : `flex flex-col sm:flex-row sm:items-center gap-2${fillHeight ? ' shrink-0' : ''}`;

  const searchInputClass = compactMobile
    ? 'toolbar-input toolbar-input--compact flex-1 min-w-0 max-w-full md:max-w-sm lg:max-w-md'
    : 'toolbar-input max-w-full sm:flex-1';

  const sizeLabelClass = compactMobile ? 'hidden md:inline text-xs text-gray-500 shrink-0' : 'text-xs text-gray-500 shrink-0';

  const chipLabel = (option) => (compactMobile ? option.compactLabel : option.label);

  return (
    <div className={fillHeight ? 'flex flex-col flex-1 h-full min-h-0 overflow-hidden gap-2' : 'space-y-2'}>
      <div className={`flex items-center gap-2 min-w-0${fillHeight ? ' shrink-0' : ''}`}>
        <span className={sizeLabelClass}>Size:</span>
        <div className={`filter-scroll min-w-0 flex-1${compactMobile ? ' filter-scroll--compact' : ''}`}>
          {CATEGORY_OPTIONS.map((option) => (
            <button
              key={option.id || 'all'}
              type="button"
              onClick={() => setCategoryFilter(option.id)}
              className={`filter-chip ${compactMobile ? 'filter-chip--compact' : ''} ${categoryFilter === option.id ? 'filter-chip-active' : ''}`}
            >
              {chipLabel(option)}
            </button>
          ))}
        </div>
      </div>

      <div className={searchRowClass}>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by model name or number…"
          className={searchInputClass}
          aria-label="Search models"
        />
        <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5 shrink-0">
          <button
            type="button"
            onClick={() => setViewMode('grid')}
            className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
              viewMode === 'grid' ? 'filter-chip filter-chip-active' : 'filter-chip'
            }`}
          >
            Grid
          </button>
          <button
            type="button"
            onClick={() => setViewMode('list')}
            className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
              viewMode === 'list' ? 'filter-chip filter-chip-active' : 'filter-chip'
            }`}
          >
            List
          </button>
        </div>
      </div>

      {filteredInventory.length === 0 ? (
        <p className={emptyStateClass}>
          No models match your search
        </p>
      ) : viewMode === 'grid' ? (
        <div className={gridScrollClass}>
          <div className={gridColsClass}>
            {filteredInventory.map(renderGridItem)}
            {footerSpacer}
          </div>
        </div>
      ) : (
        <div className={listScrollClass}>
          {filteredInventory.map(renderListItem)}
          {footerSpacer}
        </div>
      )}
    </div>
  );
};

export default ModelPicker;
